import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Clock, Play, Square, Plus, Settings as SettingsIcon, Loader2,
    Users, Calendar as CalendarIcon, History, MapPin, FileText,
    Pencil, Trash2,
} from "lucide-react";
import TopNav from "../components/TopNav";
import {
    getHourLogConfig,
    listHourLogEmployees, deleteHourLogEmployee,
    listHourLogEntries, deleteHourLogEntry,
    clockInSelf, clockOutSelf, clockInEmployee, clockOutEmployee,
} from "../lib/api";
import { getCurrentLocation } from "../lib/geolocation";

import { todayIso, fmtHMS, hoursBetween, fmtDateLabel } from "../components/hour_log/utils";
import GpsChip from "../components/hour_log/GpsChip";
import SelfView from "../components/hour_log/SelfView";
import NewEmployeeDialog from "../components/hour_log/NewEmployeeDialog";
import SettingsDialog from "../components/hour_log/SettingsDialog";
import PastPeriodsDialog from "../components/hour_log/PastPeriodsDialog";
import EntryEditDialog from "../components/hour_log/EntryEditDialog";
import EditRateDialog from "../components/hour_log/EditRateDialog";

// HourLogPage is the shell — owns top-level data (config, employees,
// entries) and orchestrates the modular dialogs / self view in
// /components/hour_log. iter-110 split this out of a 949-LOC monolith.
//
// iter-113: ``embedded`` prop lets other pages render the entire hour-log
// experience inline (used by the workgroup Hours tab). When embedded,
// the TopNav + h-dvh shell are skipped so the parent's layout owns
// the scroll and chrome.
export default function HourLogPage({ embedded = false }) {
    const [config, setConfig] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [viewerId, setViewerId] = useState("");
    const [employees, setEmployees] = useState([]);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(todayIso());
    const [newEmpOpen, setNewEmpOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [pastOpen, setPastOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [newEntryDraft, setNewEntryDraft] = useState(null); // {employee_id, date}
    const [editingRate, setEditingRate] = useState(null); // employee being edited

    const loadAll = async () => {
        setLoading(true);
        try {
            const cfg = await getHourLogConfig();
            setConfig(cfg.config);
            setIsAdmin(!!cfg.is_admin);
            setViewerId(cfg.viewer_user_id);
            const [emps, ents] = await Promise.all([
                listHourLogEmployees(),
                listHourLogEntries({}),
            ]);
            setEmployees(emps);
            setEntries(ents);
        } catch {
            toast.error("Couldn't load hour log");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); }, []);

    // Live tick so running clocks update every second.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const entriesByDate = useMemo(() => {
        const m = {};
        for (const e of entries) {
            (m[e.date] = m[e.date] || []).push(e);
        }
        return m;
    }, [entries]);

    const dayEntries = entriesByDate[selectedDate] || [];

    // Build last 14 days for the quick calendar strip
    const days = useMemo(() => {
        const out = [];
        const start = new Date();
        for (let i = 13; i >= 0; i--) {
            const d = new Date(start);
            d.setDate(start.getDate() - i);
            out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
        }
        return out;
    }, []);

    const totalHoursForEmployee = (empId) => {
        let t = 0;
        for (const e of entries) {
            if (e.employee_id !== empId) continue;
            if (!e.end_time) {
                t += hoursBetween(e.start_time, new Date(nowTick).toISOString());
            } else {
                t += hoursBetween(e.start_time, e.end_time);
            }
        }
        return t;
    };

    const handleClockToggle = async (emp) => {
        try {
            // iter-108: stamp every punch with the device's GPS coords.
            // Best-effort — if the user denied the prompt we still
            // record the entry, just without coordinates.
            const loc = await getCurrentLocation();
            const payload = loc
                ? { lat: loc.lat, lng: loc.lng, accuracy_m: loc.accuracy_m }
                : {};
            if (emp.open_entry) {
                if (isAdmin) await clockOutEmployee(emp.id, payload);
                else await clockOutSelf(payload);
                toast.success(`${emp.name} clocked out${loc ? "" : " (no GPS)"}`);
            } else {
                if (isAdmin) await clockInEmployee(emp.id, payload);
                else await clockInSelf(payload);
                toast.success(`${emp.name} clocked in${loc ? "" : " (no GPS)"}`);
            }
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clock action failed");
        }
    };

    const handleAddEntry = (empId) => {
        // iter-108: open the editor for the user to set the actual
        // start/end times — no more auto-defaulting to 9-5.
        // iter-115: pass the employee list so the dialog can offer a
        // multi-select checkbox picker (pre-seeded with this employee).
        setNewEntryDraft({
            employee_id: empId,
            date: selectedDate,
            employees,
        });
    };

    const handleAddEntryMulti = () => {
        // Multi-employee bulk add — no pre-selection.
        setNewEntryDraft({
            employee_id: null,
            date: selectedDate,
            employees,
        });
    };

    const handleDeleteEmp = async (emp) => {
        if (!window.confirm(`Remove employee "${emp.name}" and all their entries?`)) return;
        try {
            await deleteHourLogEmployee(emp.id);
            await loadAll();
            toast.success("Employee removed");
        } catch { toast.error("Couldn't remove"); }
    };

    // ===== Self-only view (members) =====
    const selfView = !isAdmin && (
        <SelfView
            viewerId={viewerId}
            employees={employees}
            entries={entries}
            onClockToggle={handleClockToggle}
            totalHoursForEmployee={totalHoursForEmployee}
        />
    );

    return (
        <div
            className={embedded ? "text-white" : "h-dvh bg-[#0A1128] text-white flex flex-col"}
            data-testid="hour-log-page"
        >
            {!embedded && <TopNav />}
            <main className={
                embedded
                    ? "w-full"
                    : "flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6 pb-40 max-w-6xl w-full mx-auto"
            }>
                <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold flex items-center gap-2">
                            <Clock className="w-3 h-3 text-[#00A3FF]" />
                            {isAdmin ? "Administrator" : "Crew member"}
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Hour log</h1>
                        <p className="text-xs text-slate-400 mt-1">
                            {isAdmin
                                ? "Track every crew member's hours and email a payroll-ready report on each pay day."
                                : "Clock in when you start, clock out when you stop. Your hours roll up into the pay report automatically."}
                        </p>
                    </div>
                    {isAdmin && (
                        <div className="flex flex-wrap items-center gap-2 justify-end">
                            <button
                                onClick={() => setPastOpen(true)}
                                data-testid="btn-past-logs"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors"
                            >
                                <History className="w-3.5 h-3.5 text-[#00A3FF]" />
                                Past logs
                            </button>
                            <button
                                onClick={() => setSettingsOpen(true)}
                                data-testid="btn-hour-log-settings"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors"
                            >
                                <SettingsIcon className="w-3.5 h-3.5 text-[#00A3FF]" />
                                Settings
                            </button>
                            <button
                                onClick={() => setNewEmpOpen(true)}
                                data-testid="btn-add-employee"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/25 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add employee
                            </button>
                        </div>
                    )}
                </header>

                {loading && (
                    <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
                )}

                {!loading && !isAdmin && selfView}

                {!loading && isAdmin && (
                    <>
                        {/* Employee roster with start/stop controls */}
                        <section className="mb-6">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2 flex items-center gap-2">
                                <Users className="w-3 h-3 text-[#00A3FF]" />
                                Crew — live clock
                            </div>
                            <ul className="space-y-2" data-testid="employee-list">
                                {employees.length === 0 && (
                                    <li className="text-slate-400 text-sm bg-[#131A2F] border border-[#2A3655] rounded-md p-4">
                                        No employees yet — tap “Add employee” to start tracking hours.
                                    </li>
                                )}
                                {employees.map((emp) => {
                                    const isOpen = !!emp.open_entry;
                                    const running = isOpen ? hoursBetween(emp.open_entry.start_time, new Date(nowTick).toISOString()) : 0;
                                    return (
                                        <li
                                            key={emp.id}
                                            data-testid={`employee-row-${emp.id}`}
                                            className={`flex flex-col sm:flex-row sm:items-center gap-3 px-3 py-3 rounded-md border ${isOpen ? "bg-emerald-500/5 border-emerald-500/50" : "bg-[#131A2F] border-[#2A3655]"}`}
                                        >
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${isOpen ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/50" : "bg-[#1C253C] text-[#00A3FF] border border-[#2A3655]"}`}>
                                                    <Clock className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-white truncate">{emp.name}</div>
                                                    <div className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-3">
                                                        {emp.email && <span className="truncate">{emp.email}</span>}
                                                        {emp.is_ghost && <span className="font-bold uppercase tracking-wider text-amber-400/80">Ghost</span>}
                                                        <span className="font-mono text-[#00A3FF]">{totalHoursForEmployee(emp.id).toFixed(2)}h total</span>
                                                        {emp.hourly_rate != null && (
                                                            <span className="font-mono text-emerald-300" data-testid={`employee-pay-${emp.id}`}>
                                                                ${(totalHoursForEmployee(emp.id) * Number(emp.hourly_rate)).toFixed(2)}
                                                                <span className="text-slate-500"> @ ${Number(emp.hourly_rate).toFixed(2)}/h</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 sm:ml-auto">
                                                {isOpen && (
                                                    <span className="text-xs font-mono text-emerald-300 tabular-nums">
                                                        {running.toFixed(2)}h
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => handleClockToggle(emp)}
                                                    data-testid={`btn-clock-${emp.id}`}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm font-bold text-[11px] uppercase tracking-wider transition-colors ${isOpen ? "bg-rose-500/20 text-rose-300 border border-rose-500/50 hover:bg-rose-500/30" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 hover:bg-emerald-500/30"}`}
                                                >
                                                    {isOpen ? <><Square className="w-3.5 h-3.5" />Stop</> : <><Play className="w-3.5 h-3.5" />Start</>}
                                                </button>
                                                <button
                                                    onClick={() => setEditingRate(emp)}
                                                    data-testid={`btn-edit-rate-${emp.id}`}
                                                    aria-label="Edit hourly wage"
                                                    title={emp.hourly_rate != null
                                                        ? `Edit wage ($${Number(emp.hourly_rate).toFixed(2)}/h)`
                                                        : "Set hourly wage"}
                                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#00A3FF]/10 border border-transparent hover:border-[#00A3FF]/30 transition-colors"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteEmp(emp)}
                                                    data-testid={`btn-delete-employee-${emp.id}`}
                                                    aria-label="Remove employee"
                                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/30 transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>

                        {/* Calendar + day entries */}
                        <section>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2 flex items-center gap-2">
                                <CalendarIcon className="w-3 h-3 text-[#00A3FF]" />
                                Date — last 14 days
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="date-strip">
                                {days.map((d) => {
                                    const has = (entriesByDate[d] || []).length > 0;
                                    const active = d === selectedDate;
                                    return (
                                        <button
                                            key={d}
                                            onClick={() => setSelectedDate(d)}
                                            data-testid={`date-chip-${d}`}
                                            className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold border transition-colors ${
                                                active
                                                    ? "bg-[#00A3FF] text-[#0A1128] border-[#00A3FF]"
                                                    : has
                                                        ? "bg-[#1C253C] border-[#00A3FF]/40 text-[#00A3FF] hover:border-[#00A3FF]"
                                                        : "bg-[#131A2F] border-[#2A3655] text-slate-400 hover:border-[#00A3FF]/40"
                                            }`}
                                        >
                                            {fmtDateLabel(d)}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="bg-[#131A2F] border border-[#2A3655] rounded-md overflow-hidden">
                                <header className="flex items-center justify-between px-4 py-2.5 border-b border-[#2A3655] bg-[#1C253C]">
                                    <div className="text-sm font-bold text-white">{fmtDateLabel(selectedDate)} — entries</div>
                                </header>
                                <div className="divide-y divide-[#2A3655]">
                                    {dayEntries.length === 0 && (
                                        <div className="px-4 py-5 text-xs text-slate-400 text-center">
                                            No entries for this day.
                                        </div>
                                    )}
                                    {dayEntries.map((e) => {
                                        const emp = employees.find((x) => x.id === e.employee_id);
                                        const hrs = e.end_time
                                            ? hoursBetween(e.start_time, e.end_time)
                                            : hoursBetween(e.start_time, new Date(nowTick).toISOString());
                                        return (
                                            <div key={e.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`entry-row-${e.id}`}>
                                                <div className="font-semibold text-white">{emp?.name || "—"}</div>
                                                <div className="font-mono text-xs text-slate-300">
                                                    {fmtHMS(e.start_time)} → {e.end_time ? fmtHMS(e.end_time) : <span className="text-emerald-300">live</span>}
                                                </div>
                                                <div className="font-mono text-xs text-[#00A3FF]">{hrs.toFixed(2)}h</div>
                                                <div className="flex items-center gap-1 ml-auto">
                                                    <button
                                                        onClick={() => setEditingEntry(e)}
                                                        data-testid={`btn-edit-entry-${e.id}`}
                                                        className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#00A3FF]/10"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={async () => { await deleteHourLogEntry(e.id); loadAll(); }}
                                                        data-testid={`btn-delete-entry-${e.id}`}
                                                        className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                {(e.job_address || e.job_description || e.clock_in_location || e.clock_out_location) && (
                                                    <div className="basis-full flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400 mt-1">
                                                        {e.job_address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.job_address}</span>}
                                                        {e.job_description && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{e.job_description}</span>}
                                                        <GpsChip loc={e.clock_in_location} kind="in" testId={`entry-clockin-gps-${e.id}`} />
                                                        <GpsChip loc={e.clock_out_location} kind="out" testId={`entry-clockout-gps-${e.id}`} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="px-3 py-2 border-t border-[#2A3655] bg-[#0F1B3A] flex flex-wrap gap-1.5 items-center">
                                    {employees.length > 1 && (
                                        <button
                                            onClick={handleAddEntryMulti}
                                            data-testid="btn-add-entry-multi"
                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] hover:bg-[#00A3FF]/30 hover:text-white text-[10px] uppercase tracking-wider font-bold"
                                            title="Add the same hours to multiple employees at once"
                                        >
                                            <Plus className="w-3 h-3" />
                                            Add for multiple
                                        </button>
                                    )}
                                    {employees.map((emp) => (
                                        <button
                                            key={emp.id}
                                            onClick={() => handleAddEntry(emp.id)}
                                            data-testid={`btn-add-entry-${emp.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#1C253C] border border-[#2A3655] hover:border-[#00A3FF] text-[10px] uppercase tracking-wider text-slate-200 hover:text-white font-bold"
                                        >
                                            <Plus className="w-3 h-3 text-[#00A3FF]" />
                                            {emp.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </>
                )}

                {/* Dialogs */}
                {isAdmin && newEmpOpen && (
                    <NewEmployeeDialog
                        open={newEmpOpen}
                        onClose={() => setNewEmpOpen(false)}
                        config={config}
                        onCreated={loadAll}
                    />
                )}
                {isAdmin && settingsOpen && (
                    <SettingsDialog
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                        config={config}
                        onSaved={(c) => { setConfig(c); setSettingsOpen(false); }}
                    />
                )}
                {isAdmin && pastOpen && (
                    <PastPeriodsDialog open={pastOpen} onClose={() => setPastOpen(false)} />
                )}
                {isAdmin && (editingEntry || newEntryDraft) && (
                    <EntryEditDialog
                        entry={editingEntry}
                        draft={newEntryDraft}
                        onClose={() => { setEditingEntry(null); setNewEntryDraft(null); }}
                        onSaved={async () => { setEditingEntry(null); setNewEntryDraft(null); await loadAll(); }}
                    />
                )}
                {isAdmin && editingRate && (
                    <EditRateDialog
                        employee={editingRate}
                        onClose={() => setEditingRate(null)}
                        onSaved={loadAll}
                    />
                )}
            </main>
        </div>
    );
}
